import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";
import Swal from "sweetalert2";
import {
  createAutomation,
  getAutomationById,
  updateAutomation,
} from "./services/AutomationServices";
import UserContext from "../../contexts/UserContext";
import { IconArrowLeft, IconDeviceFloppy } from "@tabler/icons-react";
import styled from "styled-components";

// Schema de validação
const schema = yup
  .object({
    name: yup.string().required("Nome é obrigatório"),
    account_type: yup.string().required("Tipo de conta é obrigatório"),
  })
  .required();

const AutomationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      account_type: "generic",
      active: true,
    },
  });

  // Para mostrar campos específicos baseados no tipo de conta
  const accountType = watch("account_type");

  useEffect(() => {
    if (id) {
      setInitialLoading(true);
      getAutomationById(id)
        .then((data) => {
          reset({
            name: data.name,
            description: data.description,
            account_type: data.account_type,
            active: data.active,
          });
        })
        .catch((error) => {
          console.error("Erro ao carregar automação:", error);
          Swal.fire({
            title: "Erro!",
            text: "Ocorreu um erro ao carregar a automação.",
            icon: "error",
            confirmButtonText: "OK",
          });
          navigate("/automations");
        })
        .finally(() => {
          setInitialLoading(false);
        });
    }
  }, [id, reset, navigate]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Adiciona o account_id do usuário logado
      const automationData = {
        ...data,
        account_id: user.account_id,
        user: user.user_id,
      };

      console.log("Enviando dados da automação:", automationData);

      if (id) {
        // Atualização de automação existente
        await updateAutomation(id, automationData);
        Swal.fire({
          title: "Sucesso!",
          text: "Automação atualizada com sucesso!",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        navigate("/automations");
      } else {
        // Criação de nova automação
        const response = await createAutomation(automationData);
        console.log("Resposta da criação:", response);

        const newAutomationId = response.id;

        Swal.fire({
          title: "Sucesso!",
          text: "Automação criada com sucesso!",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          // Perguntar se deseja adicionar regras
          Swal.fire({
            title: "Adicionar Regras",
            text: "Deseja adicionar regras a esta automação agora?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
          }).then((result) => {
            if (result.isConfirmed) {
              // Redirecionar para página de detalhes para adicionar regras
              navigate(`/automations/${newAutomationId}`);
            } else {
              // Voltar para lista de automações
              navigate("/automations");
            }
          });
        });
      }
    } catch (error) {
      console.error("Erro ao salvar automação:", error);

      let errorMessage = "Ocorreu um erro ao salvar a automação.";
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        const errorDetails = Object.entries(errorData)
          .map(
            ([field, messages]) =>
              `${field}: ${
                Array.isArray(messages) ? messages.join(", ") : messages
              }`
          )
          .join("; ");

        if (errorDetails) {
          errorMessage += ` Detalhes: ${errorDetails}`;
        }
      }

      Swal.fire({
        title: "Erro!",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  // Renderiza campos específicos por tipo de conta
  /* const renderAccountTypeSpecificFields = () => {
    switch (accountType) {
      case "clinic":
        return (
          <Form.Group className="mb-3">
            <Form.Label>Lembrar pacientes do jejum</Form.Label>
            <Form.Check
              type="checkbox"
              id="clinic-fasting-switch"
              label="Incluir lembretes de jejum nas mensagens de confirmação"
              {...register("clinic_settings.include_fasting")}
            />
          </Form.Group>
        );

      case "real_estate":
        return (
          <Form.Group className="mb-3">
            <Form.Label>Documentos para visita</Form.Label>
            <Form.Check
              type="switch"
              id="real-estate-documents-switch"
              label="Incluir lista de documentos necessários nas mensagens"
              {...register("real_estate_settings.include_documents")}
            />
          </Form.Group>
        );

      case "law_firm":
        return (
          <Form.Group className="mb-3">
            <Form.Label>Preparação para consulta</Form.Label>
            <Form.Check
              type="switch"
              id="law-firm-preparation-switch"
              label="Incluir instruções de preparação nas mensagens"
              {...register("law_firm_settings.include_preparation")}
            />
          </Form.Group>
        );

      default:
        return null;
    }
  }; */

  if (initialLoading) {
    return (
      <Container fluid>
        <div className="text-center p-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Carregando informações da automação...</p>
        </div>
      </Container>
    );
  }

  return (
    <ContainerWrapper>
      <Row className="mb-4">
        <Col>
          <h3>{id ? "Editar Automação" : "Nova Automação"}</h3>
          <p className="text-muted">
            {id
              ? "Atualize as informações da automação."
              : "Defina as configurações iniciais da automação."}
          </p>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Nome da Automação <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ex: Automação de Consultas Médicas"
                    {...register("name")}
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Tipo de Negócio <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    {...register("account_type")}
                    isInvalid={!!errors.account_type}
                  >
                    <option value="clinic">Clínica</option>
                    <option value="real_estate">Imobiliária</option>
                    <option value="law_firm">Advocacia</option>
                    <option value="generic">Genérico</option>
                    <option value="other">Outro</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.account_type?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Descreva o propósito desta automação..."
                {...register("description")}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="active-switch"
                label="Automação Ativa"
                {...register("active")}
              />
              <Form.Text className="text-muted">
                Se desativada, nenhuma mensagem será enviada automaticamente.
              </Form.Text>
            </Form.Group>

            {/* Campos específicos por tipo de conta */}
            {/* {renderAccountTypeSpecificFields()} */}

            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => navigate("/automations")}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={loading}
                className="d-flex align-items-center"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <IconDeviceFloppy size={18} className="me-1" />
                    Salvar
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </ContainerWrapper>
  );
};

const ContainerWrapper = styled.div`
  width: 90%;
  margin: auto;
  margin-top: 50px;
  padding: 50px;
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 30px 10px;
    height: 100%;
    overflow: auto;
  }
`;

export default AutomationForm;
